<script lang="ts">
    import { onMount } from 'svelte';
    import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
    import LineChart from '$lib/components/LineChart.svelte';
    import Slideshow from '$lib/components/Slideshow.svelte';
    import VideoPlayer from '$lib/components/VideoPlayer.svelte';
    import FeatureCell from '$lib/components/FeatureCell.svelte';
    import Footer from '$lib/components/Footer.svelte';
    import ProjectNav from '$lib/components/ProjectNav.svelte';
    import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
    import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
    import { projects, getAdjacentProjects } from '$lib/projects';
    
    let scrollY = 0;
    let translateY = 0;
    
    // Make the parallax effect reactive
    $: translateY = scrollY * 0.5;

    let titleContent: HTMLElement;
    let usersCount = 0;
    let stationsCount = 0;
    let yearsCount = 0;

    const VIDEO_URL = "https://vz-6b226b53-3c8.b-cdn.net/e59a66b1-0133-471e-b039-9946db8069f1/playlist.m3u8";

    // Gallery image arrays
    const galleryColumn1 = [{ 
        src: "/projects/db-peak-spotting/01-calendar-animated-map.png",
        alt: "Full interface overview of DB Peak Spotting"
    }];

    const galleryColumn2 = [
        { 
            src: "/projects/db-peak-spotting/02-corridor.jpg",
            alt: "Calendar view with animated map showing daily utilization patterns"
        },
        { 
            src: "/projects/db-peak-spotting/02-path-time.jpg",
            alt: "Detailed corridor analysis showing train traffic and capacity"
        }
    ];

    const galleryColumn3 = [
        { 
            src: "/projects/db-peak-spotting/03-list-large.jpg",
            alt: "Multiple map views showing network utilization"
        },
        { 
            src: "/projects/db-peak-spotting/03-list.jpg",
            alt: "List view of train schedules and capacity"
        }
    ];

    const galleryColumn4 = [
        { 
            src: "/projects/db-peak-spotting/04-2-application.png",
            alt: "Application interface with multiple data views"
        },
        { 
            src: "/projects/db-peak-spotting/02-maps-multiples-large.jpg",
            alt: "Path-time diagram showing train movements"
        }
    ];

    // Get the current project slug from the URL
    const currentSlug = 'db-peak-spotting';
    const navigation = getAdjacentProjects(currentSlug);

    onMount(() => {
        // Feature cells animation
        const featureObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('inverted');
                    }
                });
            },
            { threshold: 0.2 }
        );

        const featureCells = document.querySelectorAll('.feature-cell');
        featureCells.forEach(cell => featureObserver.observe(cell));

        // Metrics animation
        const metricsObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    animateValue(0, 5000, 1000, (val) => usersCount = val);
                    animateValue(0, 5600, 800, (val) => stationsCount = val);
                    animateValue(0, 3, 600, (val) => yearsCount = val);
                    metricsObserver.disconnect();
                }
            });
        });

        const metricsSection = document.querySelector('.metrics-section');
        if (metricsSection) {
            metricsObserver.observe(metricsSection);
        }

        // Setup scroll animations
        setupProjectAnimations();

        return () => {
            featureObserver.disconnect();
            metricsObserver.disconnect();
        };
    });
</script>


<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroSplit
    title="DB Peak Spotting"
    client="Deutsche Bahn"
    industry="Transportation"
    product="AI-powered platform for predicting and managing peak travel times"
    heroMedia={{
      type: 'video',
      src: "https://vz-6b226b53-3c8.b-cdn.net/e59a66b1-0133-471e-b039-9946db8069f1/playlist.m3u8"
    }}
    metrics={[
      { value: 15, label: "% Reduction in Delays" },
      { value: 8, label: "Months Development" },
      { value: 3, label: "Pilot Cities" }
    ]}
    roles="UX design, data science, and service design for predictive rail management."
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Project Description -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90 animate-on-view">
        Making data actionable for Germany's long distance rail network
      </p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Challenge</h2>
          <p class="mb-16 text-gray-700">
            How to process complex data to support rapid but informed decisions affecting millions of customers.
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">Value Delivered</h2>
          <p class="mb-16 text-gray-700">
            A suite of visual data-informed tools to manage Germany's rail network and its hundreds of thousands of daily passengers.
          </p>
        </div>
      </div>
    </div>

    <!-- Quote Section -->
    <section class="py-8 animate-on-view">
      <div class="container px-4 md:px-8 py-16 max-w-4xl">
        <div class="grid grid-cols-12">
          <div class="col-span-12">
            <div class="relative">
              <span 
                class="absolute -top-8 text-[120px] leading-none text-[#F96604] font-serif animate-on-view"
                aria-hidden="true"
                style="opacity: 1"
              >
                "
              </span>
              <blockquote class="py-12 text-xl md:text-4xl font-light animate-on-view" style="color: hsla(226, 65%, 35%, 1); line-height: 1.4;">
                When I first saw the visualizations, it almost had me in tears. I was finally seeing what was happening.
              </blockquote>
              <footer class="text-sm mt-4 text-gray-600">— Team Member of Deutsche Bahn AG</footer>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Full Width Image Section -->
    <div class="w-full mb-4 animate-on-view" data-delay="8">
      <div class="w-full">
        <img 
          src="/projects/db-peak-spotting/interface-full.png"
          alt="DB Peak Spotting Interface showing calendar preview, network map visualization, and detailed timeline analytics" 
          class="w-full h-auto object-contain bg-[#F5F5F5]"
          style="image-rendering: crisp-edges;"
        />
      </div>
      <div class="mt-4 px-8">
        <p class="text-sm text-gray-600">
          The full application showing the calendar preview on the left, network map visualization in the center, and detailed timeline analytics for capacity planning on the right.
        </p>
      </div>
    </div>

    <!-- Key Features -->
    <div class="w-full">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 mb-16 bg-black">
        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M4 2v26h26v2H2V2h2zm24.586 4L24 10.586L19.414 6L14 11.414L10.586 8L6 12.586v3.828l4.586-4.586L14 15.414L19.414 10l4.586 4.586L29.414 9L28 7.586z"/></svg>'
          title="Automated Dispatching"
          description="Real-time resource planning with conflict detection and resolution"
          delay={0}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M28 4H4a2 2 0 00-2 2v20a2 2 0 002 2h24a2 2 0 002-2V6a2 2 0 00-2-2zm0 2v4H4V6h24zm-24 8h24v12H4V14z"/></svg>'
          title="Predictive Maintenance"
          description="Early detection of potential issues and optimized maintenance planning"
          delay={0.2}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M16 2a14 14 0 1014 14A14 14 0 0016 2zm0 26a12 12 0 1112-12 12 12 0 01-12 12z"/></svg>'
          title="Supply Chain Optimization"
          description="Improved demand planning and accelerated supply chain processes"
          delay={0.4}
        />
      </div>
    </div>

    <!-- Full Width Key Image Section -->
    <div class="w-full mb-16 animate-on-view" data-delay="9">
      <div class="w-full">
        <img 
          src="/projects/db-peak-spotting/peakspotting-office.png"
          alt="DB Peak Spotting key visualization showing network analysis and traffic patterns" 
          class="w-full h-auto object-contain bg-[#F5F5F5]"
          style="image-rendering: crisp-edges;"
        />
      </div>
      <div class="mt-4 px-8">
        <p class="text-sm text-gray-600">
          Network visualization showing traffic patterns and capacity utilization across Germany's rail system at different times of day.
        </p>
      </div>
    </div>

    <!-- Impact Section -->
    <div class="w-full animate-on-view">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Impact</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 mb-16 bg-black">
        <!-- Results -->
        <div class="relative">
          <FeatureCell
            icon=""
            title="Economic Impact"
            description="Part of DB's AI initiative projected to save billions annually in operational costs"
            delay={0.2}
          />
          <FeatureCell
            icon=""
            title="Increased Efficiency"
            description="Enhanced punctuality and reliability through AI-driven decision making"
            delay={0.4}
          />
        </div>

        <!-- Implementation -->
        <div class="relative">
          <FeatureCell
            icon=""
            title="Network Coverage"
            description="Successfully implemented across Deutsche Bahn's entire long-distance network"
            delay={0.6}
          />
          <FeatureCell
            icon=""
            title="Future-Ready"
            description="Part of DB's strategic AI roadmap for continued innovation in rail operations"
            delay={0.8}
          />
        </div>
      </div>
    </div>

    <!-- Interface Gallery -->
    <div class="w-full mb-16">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Interface Gallery</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-8">
        <!-- Column 1 -->
        <div class="space-y-4">
          {#each galleryColumn1 as image}
            <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="0">
              <img src={image.src} alt={image.alt} class="w-full h-auto" />
            </div>
          {/each}
        </div>
        
        <!-- Column 2 -->
        <div class="space-y-4">
          {#each galleryColumn2 as image, i}
            <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="{0.1 + i * 0.1}">
              <img src={image.src} alt={image.alt} class="w-full h-auto" />
            </div>
          {/each}
        </div>
        
        <!-- Column 3 -->
        <div class="space-y-4">
          {#each galleryColumn3 as image, i}
            <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="{0.3 + i * 0.1}">
              <img src={image.src} alt={image.alt} class="w-full h-auto" />
            </div>
          {/each}
        </div>
        
        <!-- Column 4 -->
        <div class="space-y-4">
          {#each galleryColumn4 as image, i}
            <div class="overflow-hidden rounded-lg shadow-lg animate-on-view" data-delay="{0.5 + i * 0.1}">
              <img src={image.src} alt={image.alt} class="w-full h-auto" />
            </div>
          {/each}
        </div>
      </div>
    </div>

    <!-- Video Section -->
    <div class="w-full mb-16 animate-on-view">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Project Video</h2>
      <div class="aspect-video w-full overflow-hidden rounded-lg">
        <VideoPlayer
          src="https://vz-6b226b53-3c8.b-cdn.net/e59a66b1-0133-471e-b039-9946db8069f1/playlist.m3u8"
          autoplay={true}
          loop={true}
          controls={true}
          muted={true}
        />
      </div>
      <div class="mt-4 px-8">
        <p class="text-sm text-gray-600">
          Overview of the DB Peak Spotting project showcasing the interface and key features.
        </p>
      </div>
    </div>
  </div>

  <!-- Credits Section -->
  <div class="project-section border-t border-black/10 animate-on-view">
    <div class="max-w-3xl mx-auto px-8 py-12">
      <div class="flex flex-col md:flex-row justify-between items-start gap-8">
        <div>
          <p class="text-sm text-gray-500 mb-4">In collaboration with</p>
          <div class="space-y-1">
            <p class="text-sm">Moritz Stefaner (initiator)</p>
            <p class="text-sm">Christian Laesser</p>
            <p class="text-sm">Christian Au, Deutsche Bahn</p>
            <p class="text-sm">Kevin Wang, Deutsche Bahn</p>
          </div>
        </div>
        <div class="text-sm text-gray-500">
          Developed by Studio NAND
          <div class="mt-4">
            <span class="block font-semibold text-black/80 mb-2">Credits</span>
            <ul class="list-none pl-0 space-y-1">
              <li><span class="font-medium">Project director:</span> Christian Au</li>
              <li><span class="font-medium">Creative direction, data visualization:</span> Moritz Stefaner</li>
              <li><span class="font-medium">Design:</span> Christian Laesser</li>
              <li><span class="font-medium">Analytics:</span> Kevin Wang</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  {#if navigation}
    <ProjectNav 
      currentProject={{
        title: navigation.currentProject.title,
        category: navigation.currentProject.category,
        link: navigation.currentProject.link
      }}
      allProjects={projects}
    />
  {/if}

  <Footer />
</div> 